import { Card } from "@components/cards/card";
import { Pagination } from "@components/pagination";
import { MyRaffleDefaultNumberTickets } from "@components/raffle-tickets/default/number";
import { MyRaffleDefaultWordTickets } from "@components/raffle-tickets/default/word";
import { IRaffleDefaultTicketDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { IRaffleTicketFilter } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FiEye, FiMinusCircle, FiPlusCircle, FiUser } from "react-icons/fi";
import Skeleton from "react-loading-skeleton";

interface IProps {
  raffleKey: string;
  campaign?: string;
}

interface IShow {
  tickets: boolean;
}

interface ILoading {
  tickets: boolean;
}

export const RaffleTicketVisitor = ({ raffleKey, campaign }: IProps) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Filters & Paginations --- //
  const [ticketPage, setTicketPage] = useState<number>(1);
  const [ticketStatus, setTicketStatus] = useState<IRaffleTicketFilter>("e");
  const [ticketTotalPage, setTicketTotalPage] = useState<number>(0);
  const [ticketOrder, setTicketOrder] = useState<string>("ASC");
  const [ticketQuantity, setTicketQuantity] = useState<number>(15);
  const ticketQuantityStep = 5;
  const ticketQuantityMin = 5;
  const ticketQuantityMax = 50;

  function changeTicketQuantity(nextQuantity: number) {
    const quantity = Math.min(
      ticketQuantityMax,
      Math.max(ticketQuantityMin, nextQuantity),
    );

    if (quantity === ticketQuantity) return;

    setTicketPage(1);
    setTicketQuantity(quantity);
  }

  const [show, setShow] = useState<IShow>({
    tickets: false,
  });
  const [isLoading, setIsLoading] = useState<ILoading>({
    tickets: true,
  });

  const [tickets, setTickets] = useState<IRaffleDefaultTicketDTO[]>([]);

  async function loadCampaignTickets() {
    try {
      setIsLoading((prev) => ({
        ...prev,
        tickets: true,
      }));

      const params = `?campaign=${campaign}&key=${raffleKey}&order=${ticketOrder}&page=${ticketPage}&quantity=${ticketQuantity}&status=${ticketStatus}`;
      const response = await api.get(
        `/customer/raffle/default/tickets${params}`,
      );

      setTickets(response.data.ticketData);

      const totalPages = Math.max(
        1,
        Math.ceil((response?.data?.quantity || 0) / ticketQuantity),
      );
      setTicketTotalPage(totalPages);

      setIsLoading((prev) => ({
        ...prev,
        tickets: false,
      }));

      loadTicketFilter();
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações dos Tickets.",
        type: "error",
        theme: "dark",
      });
    }
  }

  const loadTicketFilter = () => {
    const Component =
      //   raffle?.type === "default_word"
      true ? MyRaffleDefaultWordTickets : MyRaffleDefaultNumberTickets;

    return (
      <Component
        tickets={tickets || []}
        filter={ticketStatus}
        ticketsForRow={4}
        ticketsAmount={2}
        atSign={undefined}
      />
    );
  };

  useEffect(() => {
    if (show.tickets && tickets?.length === 0) {
      loadCampaignTickets();
    }
  }, [show.tickets]);

  useEffect(() => {
    setIsLoading((prev) => ({
      ...prev,
      tickets: true,
    }));

    loadCampaignTickets();
  }, [ticketPage, ticketOrder, ticketQuantity, ticketStatus]);

  return (
    <>
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">Tickets da Campanha</div>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-1">
            <div className="col-md-12 text-center">
              <div className="col-md-2 mx-auto border rounded-4 py-1 mb-2 text-center">
                <span
                  className="fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setShow((prev) => ({
                      ...prev,
                      tickets: !show.tickets,
                    }))
                  }
                >
                  {show.tickets ? `Ocultar` : `Mostrar`} Tickets
                </span>
              </div>
              <div className={`${show.tickets ? `` : `d-none`} mt-4`}>
                {!tickets ? (
                  <>
                    <Skeleton count={3} />
                  </>
                ) : (
                  <>
                    {/* Filters */}
                    <div className="row justify-content-around mb-3 gy-3">
                      <div className="col-12 col-md-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-group flex-grow-1">
                            <select
                              className="form-select"
                              aria-label="Filtro de usuário"
                              value={0}
                              onChange={() => undefined}
                            >
                              <option value="0">Selecione...</option>
                              <option value="1">(Em breve)</option>
                            </select>
                          </div>
                          <FiUser
                            className="fs-4 flex-shrink-0"
                            title="Filtrar por usuário específico (em breve)."
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div
                          className="d-flex justify-content-center align-items-center gap-3"
                          title="Quantidade de tickets exibidos por página."
                        >
                          <button
                            type="button"
                            className="btn btn-link p-0 text-dark"
                            onClick={() =>
                              changeTicketQuantity(
                                ticketQuantity - ticketQuantityStep,
                              )
                            }
                            disabled={ticketQuantity <= ticketQuantityMin}
                            aria-label="Exibir menos tickets por página"
                            title="Exibir menos tickets"
                          >
                            <FiMinusCircle className="fs-4" />
                          </button>
                          <output
                            className="bg-light border rounded px-3 py-2 text-center text-muted"
                            style={{ minWidth: 72 }}
                            aria-label={ticketQuantity + " tickets por página"}
                          >
                            {ticketQuantity}
                          </output>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-dark"
                            onClick={() =>
                              changeTicketQuantity(
                                ticketQuantity + ticketQuantityStep,
                              )
                            }
                            disabled={ticketQuantity >= ticketQuantityMax}
                            aria-label="Exibir mais tickets por página"
                            title="Exibir mais tickets"
                          >
                            <FiPlusCircle className="fs-4" />
                          </button>
                        </div>
                      </div>

                      <div className="col-12 col-md-3">
                        <div className="d-flex justify-content-md-end align-items-center gap-2">
                          <FiEye
                            className="fs-5 flex-shrink-0"
                            title="Filtro de tickets exibidos."
                          />
                          <div className="form-group flex-grow-1">
                            <select
                              className="form-select"
                              aria-label="Filtro de tickets"
                              value={ticketStatus}
                              onChange={(event) => {
                                setTicketPage(1);
                                setTicketStatus(
                                  event.target.value as IRaffleTicketFilter,
                                );
                              }}
                            >
                              <option value="e">Todos</option>
                              <option value="a">Disponíveis</option>
                              <option value="u">Indisponíveis</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Pagination */}
                    <div className="row d-flex flex">
                      {/* Ticket Component: Load Options */}
                      {tickets && tickets.length > 0 ? (
                        <>
                          {loadTicketFilter()}
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="">
                              <span>
                                Página {ticketPage} de {ticketTotalPage} página
                                {ticketTotalPage > 1 ? `s` : ``}.
                              </span>
                            </div>
                            <div className="">
                              <Pagination
                                page={ticketPage}
                                setPage={setTicketPage}
                                totalPages={ticketTotalPage}
                                maxPageShow={5}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="card-body">
                            <div className="d-flex flex-column gap-2">
                              <span>Nenhum ticket foi encontrado</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};
